import {Alert, Button, Col, DatePicker, Form, Input, Row, Spin} from 'antd';
import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {
  RQGetProfileAPI,
  RQUpdateUserProfileAPI,
} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

import {OrderSummary} from './OrderSummary';

const SingleUser = () => {
  const [form] = Form.useForm();

  const [isEditing, setIsEditing] = useState(false);

  const {id} = useParams();

  const {tokenData} = useSelector(selectAuth);

  const {
    data: userDetails,
    isLoading,
    error,
    refetch,
  } = RQGetProfileAPI(id, tokenData);

  const {mutate: updateUser} = RQUpdateUserProfileAPI({
    onSuccess: () => {
      refetch();

      setIsEditing(false);

      toast.success('User profile updated successfully');
    },

    onError: error => {
      toast.error(error?.message || 'Failed to update');
    },
  });

  useEffect(() => {
    if (userDetails) {
      form.setFieldsValue({
        ...userDetails,

        dob: userDetails?.dob ? dayjs(userDetails.dob) : null,

        anniversary: userDetails?.anniversary
          ? dayjs(userDetails.anniversary)
          : null,
      });
    }
  }, [userDetails, form]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setIsEditing(false);

    form.resetFields();
  };

  const handleSave = () => {
    form

      .validateFields()

      .then(values => {
        updateUser({
          token: tokenData,

          id,

          formData: {
            ...values,

            dob: values?.dob ? values.dob.format('YYYY-MM-DD') : null,

            anniversary: values?.anniversary
              ? values.anniversary.format('YYYY-MM-DD')
              : null,
          },
        });
      })

      .catch(() => {});
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-full'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (error) {
    return <Alert message={'Error loading user'} type={'error'} />;
  }

  return (
    <div className={'p-6 flex flex-col'}>
      <div className={'flex justify-center'}>
        <Form
          form={form}
          layout={'vertical'}
          style={{
            maxWidth: 900,

            width: '100%',
          }}
          className={'p-4 rounded shadow'}
        >
          {/* avatar */}

          <div className={'pb-4 flex justify-center'}>
            <div
              className={
                'w-16 h-16 rounded-full bg-primary-900 text-white flex items-center justify-center text-xl font-semibold'
              }
            >
              {`${userDetails?.firstName?.[0] ?? ''}${userDetails?.lastName?.[0] ?? ''}`.toUpperCase()}
            </div>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={'First Name'}
                name={'firstName'}
                rules={[
                  {required: true},

                  {
                    pattern: /^[A-Za-z]+$/,

                    message: 'Only letters allowed',
                  },
                ]}
              >
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={'Last Name'}
                name={'lastName'}
                rules={[
                  {required: true},

                  {
                    pattern: /^[A-Za-z]+$/,

                    message: 'Only letters allowed',
                  },
                ]}
              >
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'Email'} name={'email'}>
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={'Mobile'}
                name={'mobile'}
                rules={[
                  {
                    pattern: /^[0-9]{10}$/,

                    message: '10 digit number required',
                  },
                ]}
              >
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'Address'} name={'address'}>
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'Street'} name={'street'}>
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'Apartment'} name={'apartment'}>
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'City'} name={'city'}>
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'District'} name={'district'}>
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={'State'} name={'state'}>
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={'Pin Code'}
                name={'pinCode'}
                rules={[
                  {
                    pattern: /^[0-9]{6}$/,

                    message: '6 digit pincode',
                  },
                ]}
              >
                <Input disabled={!isEditing} />
              </Form.Item>
            </Col>
            
          </Row>

          <Form.Item>
            {!isEditing ? (
              <Button
                className={'bg-primary-900 text-white'}
                onClick={handleEdit}
                block
              >
                Edit
              </Button>
            ) : (
              <div className={'flex gap-2'}>
                <Button
                  className={'bg-primary-900 text-white'}
                  onClick={handleSave}
                  block
                >
                  Save
                </Button>

                <Button onClick={handleCancel} block>
                  Cancel
                </Button>
              </div>
            )}
          </Form.Item>
        </Form>
      </div>

      {/* orders */}

      <div>
        <OrderSummary userId={id} />
      </div>
    </div>
  );
};

export default SingleUser;
